import GhostContentAPI from "@tryghost/content-api";
import { ghostImageUrl, ghostImageSrcset } from '../utils/image.js';

// Deduplicate an array of objects by their `id` property
function deduplicateById(arr) {
  const seen = new Set();
  return arr.filter(item => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

// localStorage cache with TTL for YouTube API responses
const YTCache = {
  TTL_PLAYLISTS: 24 * 60 * 60 * 1000,
  TTL_VIDEOS: 2 * 60 * 60 * 1000,

  set(key, value, ttl) {
    try {
      localStorage.setItem(key, JSON.stringify({ v: value, exp: Date.now() + ttl }));
    } catch (e) { /* localStorage full or unavailable */ }
  },

  get(key) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const { v, exp } = JSON.parse(raw);
      if (Date.now() > exp) { localStorage.removeItem(key); return null; }
      return v;
    } catch (e) { return null; }
  },

  playlistsKey: (channelId) => `yt_pl_${channelId}`,
  videosKey: (playlistId) => `yt_pv_${playlistId}`,
};

// Scan all yt_pv_* localStorage entries and return sorted, deduplicated videos
Object.assign(YTCache, {
  getAllCachedVideos() {
    const videos = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key?.startsWith('yt_pv_')) continue;
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        if (Date.now() > parsed.exp) continue;
        if (parsed.v?.videos) videos.push(...parsed.v.videos);
      }
    } catch (e) { /* ignore */ }
    return deduplicateById(videos).sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
  }
});

// Tracks YouTube API quota exhaustion. Persists until YouTube's daily reset (midnight PT ≈ 08:00 UTC).
const YTQuotaState = {
  KEY: 'yt_quota_exhausted',

  isExhausted() {
    try {
      const raw = localStorage.getItem(this.KEY);
      if (!raw) return false;
      const { exp } = JSON.parse(raw);
      if (Date.now() > exp) { localStorage.removeItem(this.KEY); return false; }
      return true;
    } catch (e) { return false; }
  },

  setExhausted() {
    try {
      // YouTube quota resets at midnight Pacific Time (PST=UTC-8, PDT=UTC-7).
      const isPDT = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', timeZoneName: 'short' })
        .formatToParts(new Date())
        .some(p => p.type === 'timeZoneName' && p.value === 'PDT');
      const resetUTCHour = isPDT ? 7 : 8;
      const nextReset = new Date();
      nextReset.setUTCHours(resetUTCHour, 0, 0, 0);
      if (nextReset.getTime() <= Date.now()) nextReset.setUTCDate(nextReset.getUTCDate() + 1);
      localStorage.setItem(this.KEY, JSON.stringify({ exp: nextReset.getTime() }));
    } catch (e) { /* ignore */ }
  },
};

// Cards List Component Manager
export class CardsListManager {
  constructor() {
    this.config = window.GhostConfig || {};
    this.sections = new Map();
    this.init();
  }

  init() {
    const sections = document.querySelectorAll('.cards-list[data-scenario]');
    if (!sections.length) return;
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        obs.unobserve(entry.target);
        const manager = this.sections.get(entry.target);
        if (manager) manager.init();
      });
    }, { rootMargin: '200px' });
    sections.forEach((section) => {
      const scenario = section.getAttribute('data-scenario');
      const manager = this.createScenarioManager(section, scenario);
      if (manager) {
        this.sections.set(section, manager);
        observer.observe(section);
      }
    });
  }

  createScenarioManager(section, scenario) {
    switch (scenario) {
      case 'articles': return new ArticlesCardsManager(section, this.config);
      case 'videos':   return new VideosCardsManager(section, this.config);
      case 'mixed':    return new MixedCardsManager(section, this.config);
      case 'author':   return new AuthorCardsManager(section, this.config);
      default:
        console.warn(`Unknown cards-list scenario: ${scenario}`);
        return null;
    }
  }
}

// Base class for all scenario managers
export class BaseCardsManager {
  constructor(section, config) {
    this.section = section;
    this.config = config;
    this.api = new GhostContentAPI({
      url: config.apiUrl,
      key: config.contentKey,
      version: 'v5.0'
    });

    // Card rendering
    this.cardType = section.getAttribute('data-card-type') || 'small';
    this.cardClass = section.getAttribute('data-card-class') || '';

    // Pagination
    this.limit = section.getAttribute('data-limit') || '4';
    this.infiniteScroll = section.getAttribute('data-infinite-scroll') === 'true';

    // Article filter config (used by Articles and Mixed scenarios)
    this.formats = section.getAttribute('data-formats') || '';
    this.types = section.getAttribute('data-types') || '';
    this.filterByAuthors = section.getAttribute('data-filter-by-authors') === 'true';
    this.filterByFormats = section.getAttribute('data-filter-by-formats') === 'true';
    this.excludeFormats = section.getAttribute('data-exclude-formats') || '';
    this.formatsLabel = section.getAttribute('data-formats-label') || '';

    // Article filter state
    this.selectedFormats = [];
    this.selectedTypes = [];
    this.selectedAuthors = [];
    this.allFormats = [];
    this.allTypes = [];
    this.allAuthors = [];

    // DOM refs
    this.articlesContainer = section.querySelector('.cards-list__articles');
    this.filterList = section.querySelector('.cards-filter__list');
    this.popup = section.querySelector('.filter-popup');
    this.filterButton = section.querySelector('.cards-filter__button');
    this.popupContainer = section.querySelector('.cards-filter__popup-container');

    // Pagination state
    this.isLoading = false;
    this.currentPage = 1;
    this.hasMorePosts = true;
    this.allPosts = [];
  }

  init() {
    this.setupEventListeners();
    this.loadInitialContent();
    this.setupInfiniteScroll();
    setTimeout(() => this.checkAndLoadMoreIfNeeded(), 100);
  }

  applyFormatsLabel() {
    if (!this.formatsLabel) return;
    const title = this.section.querySelector('.filter-popup__column .filter-popup__title');
    if (title) title.textContent = this.formatsLabel;
  }

  checkAndLoadMoreIfNeeded() {
    if (!this.infiniteScroll || this.isLoading || !this.hasMorePosts) return;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    if (scrollTop + windowHeight >= documentHeight - 400) {
      this.loadMorePosts();
    }
  }

  setupEventListeners() {}
  loadInitialContent() {}
  async loadMorePosts() {}

  setupInfiniteScroll() {
    if (!this.infiniteScroll) return;
    this.scrollHandler = () => this.handleScroll();
    window.addEventListener('scroll', this.scrollHandler);
  }

  handleScroll() {
    if (!this.infiniteScroll || this.isLoading || !this.hasMorePosts) return;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    if (scrollTop + windowHeight >= documentHeight - 200) {
      this.loadMorePosts();
    }
  }

  // ── Filter popup UI ──────────────────────────────────────────────────────────

  setupFilterPopup() {
    if (!this.filterButton || !this.popup) return;

    this.filterButton.addEventListener('click', e => {
      e.preventDefault();
      this.togglePopup();
    });

    this.popup.addEventListener('change', e => {
      if (e.target.classList.contains('filter-popup__checkbox')) {
        this.handleCheckboxChange(e);
      }
    });

    const resetButton = this.popup.querySelector('.filter-popup__cancel-btn');
    if (resetButton) {
      resetButton.addEventListener('click', e => {
        e.preventDefault();
        this.resetAllFilters();
      });
    }

    document.addEventListener('click', e => {
      if (this.popup?.classList.contains('show') && !this.popupContainer?.contains(e.target)) {
        this.closePopup();
      }
    });
  }

  /** Override in subclasses to handle checkbox changes in the popup */
  handleCheckboxChange(_e) {}

  /** Override in subclasses to populate popup content when it opens */
  onPopupOpen() {}

  togglePopup() {
    if (!this.popup) return;
    if (this.popup.classList.contains('show')) {
      this.closePopup();
    } else {
      this.popup.classList.add('show');
      this.onPopupOpen();
    }
    this.updateFilterButton();
  }

  closePopup() {
    this.popup?.classList.remove('show');
    this.updateFilterButton();
  }

  updateFilterButton() {
    if (!this.filterButton) return;
    const buttonText = this.filterButton.querySelector('span');
    const filterIcon = this.filterButton.querySelector('.filter-icon');
    const closeIcon = this.filterButton.querySelector('.close-icon');
    const isVisible = this.popup?.classList.contains('show');
    if (filterIcon) filterIcon.style.display = isVisible ? 'none' : 'block';
    if (closeIcon) closeIcon.style.display = isVisible ? 'block' : 'none';
    if (buttonText) buttonText.textContent = isVisible ? '閉じる' : '絞り込み';
  }

  // ── Article filter data loading ──────────────────────────────────────────────

  async loadFormats() {
    if (this.allFormats.length > 0) return;
    try {
      const response = await this.api.tags.browse({
        filter: `slug:[${this.formats}]`,
        limit: 'all'
      });
      this.allFormats = response.map(tag => ({ slug: tag.slug, name: tag.name }));
      this.renderFormatsList();
    } catch (error) {
      console.error('Failed to load formats:', error);
    }
  }

  async loadTypes() {
    if (!this.types) return;
    const typeSlugs = this.types.split(',');
    this.allTypes = typeSlugs.map(slug => ({
      slug: slug.trim(),
      name: slug.trim().charAt(0).toUpperCase() + slug.trim().slice(1).replace(/-/g, ' ')
    }));
    this.renderTypesList();
  }

  // Ghost caps `limit` at 100 no matter what you ask for — `limit: 'all'` included —
  // and returns the first page with no error, so an archive over 100 posts is silently
  // truncated (OPS-1097: the author dropdown was built from 100 of 238 posts and was
  // missing 5 of 17 authors). Page 1 reports the page count, so the rest go out
  // together rather than as a chain. Same shape as fetchPosts() in search.js.
  async fetchAllPosts(options) {
    // Spread first: `limit` and `page` drive the loop, and letting a caller pin either
    // would leave this requesting page 1 forever.
    const browse = (page) => this.api.posts.browse({ ...options, limit: 100, page });

    const first = await browse(1);
    if (!first || first.length === 0) return [];

    const { pages } = first.meta?.pagination || {};
    if (!pages || pages <= 1) return [...first];

    const rest = await Promise.all(
      Array.from({ length: pages - 1 }, (_, index) => browse(index + 2))
    );

    return rest.reduce((all, page) => all.concat(page || []), [...first]);
  }

  async loadAuthors() {
    try {
      // Only post.authors is read below, so project the post itself away. Without
      // `fields` Ghost returns all 36 columns including each post's rendered `html`,
      // which was ~86% of a 659 KB response on the homepage — for a filter dropdown
      // (OPS-1096). `include` survives a projection, so the authors still arrive.
      const response = await this.fetchAllPosts({
        include: ['authors'],
        fields: 'id',
        filter: `tag:[${this.formats}]+tag:-hash-hide`
      });
      const authorsMap = new Map();
      response.forEach(post => {
        post.authors?.forEach(author => {
          if (!authorsMap.has(author.slug)) {
            authorsMap.set(author.slug, { slug: author.slug, name: author.name });
          }
        });
      });
      this.allAuthors = Array.from(authorsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
      this.renderAuthorsList();
    } catch (error) {
      console.error('Failed to load authors:', error);
    }
  }

  // ── Article filter rendering ─────────────────────────────────────────────────

  renderFormatsList() {
    const el = this.section.querySelector('#formats-list');
    if (!el) return;
    this.applyFormatsLabel();
    el.innerHTML = this.allFormats.map(format => `
      <label class="filter-popup__option">
        <input type="checkbox" class="filter-popup__checkbox" data-format-slug="${format.slug}" ${this.selectedFormats.some(f => f.slug === format.slug) ? 'checked' : ''}>
        <span class="filter-popup__text">${format.name}</span>
      </label>`).join('');
  }

  renderTypesList() {
    const el = this.section.querySelector('#types-list');
    if (!el) return;
    el.innerHTML = this.allTypes.map(type => `
      <label class="filter-popup__option">
        <input type="checkbox" class="filter-popup__checkbox" data-type-slug="${type.slug}" ${this.selectedTypes.some(t => t.slug === type.slug) ? 'checked' : ''}>
        <span class="filter-popup__text">${type.name}</span>
      </label>`).join('');
  }

  renderAuthorsList() {
    const el = this.section.querySelector('#authors-list');
    if (!el) return;
    el.innerHTML = this.allAuthors.map(author => `
      <label class="filter-popup__option">
        <input type="checkbox" class="filter-popup__checkbox" data-author-slug="${author.slug}" ${this.selectedAuthors.some(a => a.slug === author.slug) ? 'checked' : ''}>
        <span class="filter-popup__text">${author.name}</span>
      </label>`).join('');
  }

  // ── Article filter state ─────────────────────────────────────────────────────

  handleFilterChange() {
    this.selectedFormats = this._readCheckedItems('#formats-list', 'data-format-slug', this.allFormats);
    this.selectedTypes   = this._readCheckedItems('#types-list',   'data-type-slug',   this.allTypes);
    this.selectedAuthors = this._readCheckedItems('#authors-list', 'data-author-slug', this.allAuthors);
    this.onFilterChanged();
  }

  _readCheckedItems(listSelector, dataAttr, allItems) {
    const result = [];
    this.section.querySelectorAll(`${listSelector} input[type="checkbox"]`).forEach(cb => {
      if (cb.checked) {
        const item = allItems.find(i => i.slug === cb.getAttribute(dataAttr));
        if (item) result.push(item);
      }
    });
    return result;
  }

  /** Override in subclasses to react after article filter checkboxes change */
  onFilterChanged() {}

  updatePopupCheckboxes() {
    this.section.querySelectorAll('#formats-list input[type="checkbox"]').forEach(cb => {
      cb.checked = this.selectedFormats.some(f => f.slug === cb.getAttribute('data-format-slug'));
    });
    this.section.querySelectorAll('#types-list input[type="checkbox"]').forEach(cb => {
      cb.checked = this.selectedTypes.some(t => t.slug === cb.getAttribute('data-type-slug'));
    });
    this.section.querySelectorAll('#authors-list input[type="checkbox"]').forEach(cb => {
      cb.checked = this.selectedAuthors.some(a => a.slug === cb.getAttribute('data-author-slug'));
    });
  }

  // ── Card rendering ───────────────────────────────────────────────────────────

  getImageSizes() {
    const cls = this.cardClass;
    if (cls.includes('card--related')) {
      return '(max-width: 900px) 80vw, (max-width: 1024px) 50vw, (max-width: 1520px) 27.5vw, 400px';
    }
    if (cls.includes('card--default-smaller')) {
      return '(max-width: 600px) 100vw, (max-width: 900px) 50vw, (max-width: 1280px) 30vw, (max-width: 1520px) 25vw, 400px';
    }
    if (this.cardType === 'small') {
      return '(min-width: 600px) 225px, 150px';
    }
    // regular default
    return '(max-width: 900px) 80vw, (max-width: 1520px) 30vw, 625px';
  }

  renderCard(post) {
    const SRCSET_WIDTHS = [300, 400, 600, 800, 1200];
    const featureImage = post.feature_image ? `
      <a href="${post.url}" class="card__image">
        <img
          data-src="${ghostImageUrl(post.feature_image, 600)}"
          data-srcset="${ghostImageSrcset(post.feature_image, SRCSET_WIDTHS)}"
          data-sizes="${this.getImageSizes()}"
          class="lazyload"
          alt="${post.title}"
        >
      </a>` : '';
    const tagHtml = post.primary_tag ? `<span class="card__tag">${post.primary_tag.name}</span>` : '';
    const readingTime = post.reading_time ?
      `<span class="card__reading-time">${post.reading_time}分で読めます</span>` : '';
    const date = new Date(post.published_at);
    const dateStr = date.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });

    if (this.cardType === 'default') {
      const excerpt = post.custom_excerpt ? `<p class="card__description">${post.custom_excerpt}</p>` : '';
      return `
        <article class="card card--default flex direction-column ${this.cardClass}">
          ${featureImage}
          <div class="card__main flex direction-column">
            <h3 class="card__title"><a href="${post.url}">${post.title}</a></h3>
            ${excerpt}
            <div class="card__footer flex items-center">
              ${tagHtml}
              <div class="card__meta flex items-center">
                <span class="card__date">${dateStr}</span>
                ${readingTime}
              </div>
            </div>
          </div>
        </article>`;
    } else {
      return `
        <article class="card card--small flex items-center ${this.cardClass}">
          ${featureImage}
          <div class="card__main flex">
            <h3 class="card__title"><a href="${post.url}">${post.title}</a></h3>
            ${tagHtml}
            <div class="card__meta flex items-center">
              <span class="card__date">${dateStr}</span>
              ${readingTime}
            </div>
          </div>
        </article>`;
    }
  }

  /** Override in subclasses to render a different item type (e.g. video card) */
  renderItem(item) {
    return this.renderCard(item);
  }

  renderPosts(items, append = false) {
    const html = items.map(item => this.renderItem(item)).join('');

    if (append) {
      this.articlesContainer.insertAdjacentHTML('beforeend', html);
      setTimeout(() => this.checkAndLoadMoreIfNeeded(), 100);
    } else {
      this.articlesContainer.style.transition = 'opacity 0.15s ease';
      this.articlesContainer.style.opacity = '0';
      setTimeout(() => {
        this.articlesContainer.innerHTML = html || '<p>記事が見つかりませんでした。</p>';
        this.articlesContainer.style.minHeight = '';
        this.articlesContainer.style.opacity = '1';
        setTimeout(() => this.checkAndLoadMoreIfNeeded(), 100);
      }, 150);
    }
  }
}

// Articles-only scenario
export class ArticlesCardsManager extends BaseCardsManager {
  constructor(section, config) {
    super(section, config);
    // All filter config and state is initialized in BaseCardsManager
  }

  setupEventListeners() {
    super.setupEventListeners();
    if (this.filterList) {
      this.filterList.addEventListener('click', e => {
        const button = e.target.closest('button.cards-filter__item-button');
        if (!button) return;
        e.preventDefault();
        this.handleFilterClick(button);
      });
    }
    this.setupFilterPopup();
  }

  loadInitialContent() {
    this._preloadFilterData();
  }

  async _preloadFilterData() {
    const promises = [this.loadFormats()];
    if (this.types) promises.push(this.loadTypes());
    if (this.filterByAuthors) promises.push(this.loadAuthors());
    await Promise.all(promises);
  }

  handleCheckboxChange(_e) {
    this.handleFilterChange();
  }

  onPopupOpen() {
    this.renderFormatsList();
    if (this.types) this.renderTypesList();
    if (this.filterByAuthors) this.renderAuthorsList();
  }

  onFilterChanged() {
    this.updateMainFormatButtons();
    this.loadPostsByTag('all');
  }

  handleFilterClick(button) {
    const slug = button.getAttribute('data-slug');

    this.filterList.querySelectorAll('.cards-filter__item-button').forEach(btn => btn.classList.remove('active'));
    button.classList.add('active');

    this.selectedFormats = [];
    this.selectedTypes = [];
    this.selectedAuthors = [];
    if (slug !== 'all') {
      const format = this.allFormats.find(f => f.slug === slug);
      if (format) this.selectedFormats = [format];
    }

    this.closePopup();
    this.updatePopupCheckboxes();
    this.loadPostsByTag(slug);
  }

  updateMainFormatButtons() {
    this.filterList.querySelectorAll('.cards-filter__item-button').forEach(button => {
      const slug = button.getAttribute('data-slug');
      if (slug === 'all') {
        button.classList.toggle('active', this.selectedFormats.length === 0);
      } else {
        button.classList.toggle('active', this.selectedFormats.some(f => f.slug === slug));
      }
    });
  }

  resetAllFilters() {
    this.selectedFormats = [];
    this.selectedTypes = [];
    this.selectedAuthors = [];
    this.section.querySelectorAll('.filter-popup__checkbox').forEach(cb => cb.checked = false);
    this.updateMainFormatButtons();
    this.closePopup();
    this.loadPostsByTag('all');
  }

  async loadPostsByTag(slug, page = 1, append = false) {
    if (this.isLoading) return;
    try {
      this.isLoading = true;

      let filter = '';
      if (slug !== 'all') {
        filter = `tag:${slug}`;
      } else if (this.filterByFormats && this.formats) {
        filter = `tag:[${this.formats}]`;
      }

      if (this.selectedFormats.length > 0) {
        const f = this.selectedFormats.map(f => f.slug).join(',');
        filter = filter ? `${filter}+tag:[${f}]` : `tag:[${f}]`;
      }
      if (this.selectedTypes.length > 0) {
        const t = this.selectedTypes.map(t => t.slug).join(',');
        filter = filter ? `${filter}+tag:[${t}]` : `tag:[${t}]`;
      }
      if (this.selectedAuthors.length > 0) {
        const slugs = this.selectedAuthors.map(a => a.slug).join(',');
        const authorFilter = this.selectedAuthors.length === 1
          ? `authors:${slugs}`
          : `authors:[${slugs}]`;
        filter = filter ? `${filter}+${authorFilter}` : authorFilter;
      }
      if (this.excludeFormats) {
        const exclude = `tag:-[${this.excludeFormats}]`;
        filter = filter ? `${filter}+${exclude}` : exclude;
      }
      filter = filter ? `${filter}+tag:-hash-hide` : 'tag:-hash-hide';

      const options = {
        include: ['tags', 'authors'],
        page,
        limit: this.limit === 'all' ? 15 : parseInt(this.limit)
      };
      if (filter) options.filter = filter;

      const posts = await this.api.posts.browse(options);

      if (append) {
        const existingIds = new Set(this.allPosts.map(p => p.id));
        const newPosts = posts.filter(p => !existingIds.has(p.id));
        this.allPosts = [...this.allPosts, ...newPosts];
        this.renderPosts(newPosts, true);
      } else {
        this.allPosts = posts;
        this.renderPosts(posts, false);
      }

      this.currentPage = page;
      this.hasMorePosts = posts.length === parseInt(this.limit);
    } catch (error) {
      console.error('Failed to load posts:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async loadMorePosts() {
    if (!this.infiniteScroll || this.isLoading || !this.hasMorePosts) return;
    const activeButton = this.filterList.querySelector('.cards-filter__item-button.active');
    const currentSlug = activeButton?.getAttribute('data-slug') ?? 'all';
    await this.loadPostsByTag(currentSlug, this.currentPage + 1, true);
  }
}

// Videos-only scenario.
// YouTube API calls go through the Cloudflare Worker proxy at /api/youtube/*
// (worker route: blog.gtowizard.com/api/youtube/*; source:
// terraform/cloudflare/gtowizard/workers/scripts/youtube_api_proxy.js).
// The proxy holds the API key as a Worker secret and edge-caches responses
// (24h playlists, 2h playlistItems). YTCache below is the per-browser
// localStorage layer that stacks on top of the edge cache.
export class VideosCardsManager extends BaseCardsManager {
  constructor(section, config) {
    super(section, config);
    this.youtubeChannelId = config.youtubeChannelId;
    this.allPlaylists = [];
    this.selectedPlaylists = [];

    // Infinite scroll state
    this.allVideos = [];
    this.videoCache = new Map();
    this.playlistPageTokens = {};
    this.playlistExhausted = new Set();
    this.displayedVideoIds = new Set();
    this.videoBuffer = [];
    this.isInitialLoad = true;

    // Offline mode state
    this.offlineVideos = [];
    this.offlineDisplayedCount = 0;
  }

  setupEventListeners() {
    super.setupEventListeners();
    if (this.filterList) {
      this.filterList.addEventListener('click', e => {
        const button = e.target.closest('button.cards-filter__item-button');
        if (!button) return;
        e.preventDefault();
        this.handleFilterClick(button);
      });
    }
    this.setupFilterPopup();
  }

  handleCheckboxChange(e) {
    if (e.target.hasAttribute('data-yplaylist')) this.handlePlaylistChange();
  }

  onPopupOpen() {
    this.renderPlaylistsInPopup();
  }

  renderItem(video) {
    return this.renderVideoCard(video);
  }

  async loadInitialContent() {
    await this.loadPlaylists();
    this.renderPlaylistButtons();
    this.loadVideos();
  }

  async loadPlaylists() {
    if (!this.youtubeChannelId || this.youtubeChannelId.includes('{{') || this.youtubeChannelId === 'undefined') {
      console.warn('YouTube channel ID not configured or invalid');
      this.allPlaylists = [];
      return;
    }
    const cacheKey = YTCache.playlistsKey(this.youtubeChannelId);
    const cached = YTCache.get(cacheKey);
    if (cached) {
      this.allPlaylists = cached.filter(pl => pl.title.toLowerCase() !== 'shorts');
      return;
    }
    if (YTQuotaState.isExhausted()) {
      this.allPlaylists = [];
      return;
    }
    try {
      const url = `/api/youtube/playlists?part=snippet,contentDetails&channelId=${this.youtubeChannelId}&maxResults=50`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.error) {
        if (data.error.code === 403) YTQuotaState.setExhausted();
        else console.error('YouTube API error:', data.error);
        this.allPlaylists = [];
        return;
      }
      if (data.items) {
        this.allPlaylists = data.items
          .map(pl => ({
            id: pl.id,
            title: pl.snippet.title,
            count: pl.contentDetails?.itemCount || 0
          }))
          .filter(pl => pl.title.toLowerCase() !== 'shorts');
        YTCache.set(cacheKey, this.allPlaylists, YTCache.TTL_PLAYLISTS);
        this.renderPlaylistButtons();
      }
    } catch (error) {
      console.error('Failed to load playlists:', error);
      this.allPlaylists = [];
    }
  }

  renderPlaylistButtons() {
    if (!this.filterList) return;
    const topPlaylists = [...this.allPlaylists].sort((a, b) => b.count - a.count).slice(0, 4);
    const allButton = this.filterList.querySelector('li:first-child');
    this.filterList.innerHTML = '';
    if (allButton) this.filterList.appendChild(allButton);
    this.filterList.insertAdjacentHTML('beforeend', topPlaylists.map(pl =>
      `<li class="cards-filter__item">
        <button class="cards-filter__item-button" data-playlist-id="${pl.id}">${pl.title}</button>
      </li>`
    ).join(''));
  }

  handleFilterClick(button) {
    const playlistId = button.getAttribute('data-playlist-id');
    const slug = button.getAttribute('data-slug');
    this.filterList.querySelectorAll('.cards-filter__item-button').forEach(btn => btn.classList.remove('active'));
    button.classList.add('active');
    if (slug === 'all') {
      this.selectedPlaylists = [];
    } else if (playlistId) {
      this.selectedPlaylists = [playlistId];
    }
    this.closePopup();
    this.loadVideos();
  }

  loadVideos() {
    if (YTQuotaState.isExhausted()) {
      this.loadVideosOffline();
    } else {
      this.loadVideosOnline();
    }
  }

  async loadVideosOnline() {
    if (this.isLoading) return;
    try {
      this.isLoading = true;
      this.articlesContainer.classList.add('cards-list__articles--loading');
      this.articlesContainer.classList.remove('cards-list__articles--empty', 'cards-list__articles--error');

      this.displayedVideoIds.clear();
      this.videoBuffer = [];
      this.isInitialLoad = true;

      if (this.selectedPlaylists.length > 0) {
        const keep = new Set(this.selectedPlaylists);
        for (const [id] of this.videoCache) {
          if (!keep.has(id)) {
            this.videoCache.delete(id);
            this.playlistPageTokens[id] = null;
            this.playlistExhausted.delete(id);
          }
        }
      }

      let { videos, hasMore } = await this.fetchYouTubeVideos(false);
      this.articlesContainer.classList.remove('cards-list__articles--loading');

      // If quota was hit during fetch, switch to offline mode
      if (YTQuotaState.isExhausted()) {
        this.isLoading = false; // release lock so loadVideosOffline can acquire it
        await this.loadVideosOffline();
        return;
      }

      videos.forEach(v => this.displayedVideoIds.add(v.id));
      this.allVideos = videos;
      this.hasMorePosts = hasMore;

      const hasLoading = this.articlesContainer.querySelector('.cards-loading');
      if (videos.length === 0) {
        this.articlesContainer.classList.add('cards-list__articles--empty');
        this.articlesContainer.innerHTML = '<p class="cards-list__empty-message">動画が見つかりませんでした。</p>';
      } else {
        this.articlesContainer.classList.remove('cards-list__articles--empty', 'cards-list__articles--error');
        if (hasLoading) {
          this.articlesContainer.innerHTML = videos.map(v => this.renderVideoCard(v)).join('');
        } else {
          this.renderPosts(videos, false);
        }
      }
    } catch (error) {
      console.error('Failed to load videos:', error);
      this.articlesContainer.classList.remove('cards-list__articles--loading');
      this.articlesContainer.classList.add('cards-list__articles--error');
      this.articlesContainer.innerHTML = '<p class="cards-list__error-message">動画の読み込みに失敗しました。時間をおいて再度お試しください。</p>';
    } finally {
      this.isLoading = false;
    }
  }

  async loadVideosOffline() {
    if (this.isLoading) return;
    this.isLoading = true;
    try {
      // Lock height and fade out before swapping content to prevent layout jump
      const h = this.articlesContainer.offsetHeight;
      if (h > 0) this.articlesContainer.style.minHeight = `${h}px`;
      this.articlesContainer.style.transition = 'opacity 0.15s ease';
      this.articlesContainer.style.opacity = '0';
      await new Promise(r => setTimeout(r, 160));

      // Clear content first — before removing --empty (which holds centering), so nothing jumps
      this.articlesContainer.innerHTML = '';
      this.articlesContainer.classList.remove('cards-list__articles--empty', 'cards-list__articles--error');

      this.displayedVideoIds.clear();
      this.videoBuffer = [];
      const perPage = parseInt(this.limit) || 6;

      let videos;
      if (this.selectedPlaylists.length > 0) {
        const raw = [];
        for (const id of this.selectedPlaylists) {
          const cached = YTCache.get(YTCache.videosKey(id));
          if (cached?.videos) raw.push(...cached.videos);
        }
        videos = deduplicateById(raw).sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
      } else {
        videos = YTCache.getAllCachedVideos();
      }

      this.offlineVideos = videos;
      this.offlineDisplayedCount = 0;
      this.articlesContainer.classList.remove('cards-list__articles--loading');

      if (videos.length === 0) {
        this.articlesContainer.classList.add('cards-list__articles--empty');
        const msg = this.selectedPlaylists.length > 0
          ? 'このプレイリストの動画はまだキャッシュされていません。復旧に向けて作業中です。しばらく経ってから再度お試しください。'
          : '動画が一時的に利用できません。復旧に向けて作業中です。しばらく経ってから再度お試しください。';
        this.articlesContainer.innerHTML = `<p class="cards-list__empty-message">${msg}</p>`;
        this.hasMorePosts = false;
        // Hide filter controls when no videos at all are available
        if (this.selectedPlaylists.length === 0) {
          const filterSection = this.section.querySelector('.cards-list__filter');
          if (filterSection) filterSection.style.display = 'none';
        }
      } else {
        const slice = videos.slice(0, perPage);
        this.offlineDisplayedCount = slice.length;
        slice.forEach(v => this.displayedVideoIds.add(v.id));
        this.articlesContainer.classList.remove('cards-list__articles--empty', 'cards-list__articles--error');
        const hasLoading = this.articlesContainer.querySelector('.cards-loading');
        if (hasLoading) {
          this.articlesContainer.innerHTML = slice.map(v => this.renderVideoCard(v)).join('');
        } else {
          this.renderPosts(slice, false);
        }
        this.hasMorePosts = videos.length > perPage;
      }

      this.articlesContainer.style.minHeight = '';
      this.articlesContainer.style.transition = '';
      this.articlesContainer.style.opacity = '1';
    } catch (error) {
      console.error('Failed to load cached videos:', error);
      this.articlesContainer.classList.remove('cards-list__articles--loading');
      this.articlesContainer.classList.add('cards-list__articles--error');
      this.articlesContainer.innerHTML = '<p class="cards-list__error-message">Failed to load videos.</p>';
      this.articlesContainer.style.minHeight = '';
      this.articlesContainer.style.transition = '';
      this.articlesContainer.style.opacity = '1';
    } finally {
      this.isLoading = false;
    }
  }

  async fetchPlaylistVideos(playlistId, pageToken = null) {
    const playlist = this.allPlaylists.find(p => p.id === playlistId);
    if (!playlist) return { videos: [], nextPageToken: null };
    if (this.playlistExhausted.has(playlistId) && !pageToken) return { videos: [], nextPageToken: null };

    // Check localStorage cache only for first page fetches
    if (!pageToken) {
      const cacheKey = YTCache.videosKey(playlistId);
      const cached = YTCache.get(cacheKey);
      if (cached) {
        if (cached.exhausted) this.playlistExhausted.add(playlistId);
        else if (cached.nextPageToken) this.playlistPageTokens[playlistId] = cached.nextPageToken;
        return { videos: cached.videos, nextPageToken: cached.nextPageToken || null };
      }
    }

    let url = `/api/youtube/playlistItems?part=snippet,contentDetails,status&maxResults=50&playlistId=${playlistId}`;
    if (pageToken) url += `&pageToken=${pageToken}`;

    try {
      const response = await fetch(url);
      const data = await response.json();
      if (data.error) {
        if (data.error.code === 403) YTQuotaState.setExhausted();
        else console.error('YouTube API error:', data.error);
        this.playlistExhausted.add(playlistId);
        return { videos: [], nextPageToken: null };
      }
      if (!data.items || data.items.length === 0) {
        this.playlistExhausted.add(playlistId);
        return { videos: [], nextPageToken: null };
      }

      const videos = data.items
        .filter(item => {
          const title = item.snippet?.title || '';
          if (!title || title === 'Deleted video' || title === 'Private video' || title === 'This video is unavailable') return false;
          if (item.status?.privacyStatus) return item.status.privacyStatus === 'public';
          return true;
        })
        .map(item => ({
          id: item.contentDetails?.videoId || item.snippet?.resourceId?.videoId,
          url: `https://www.youtube.com/watch?v=${item.contentDetails?.videoId || item.snippet?.resourceId?.videoId}`,
          title: item.snippet.title,
          published_at: item.snippet.publishedAt,
          feature_image: item.snippet.thumbnails?.maxres?.url ||
                         item.snippet.thumbnails?.standard?.url ||
                         item.snippet.thumbnails?.high?.url ||
                         item.snippet.thumbnails?.medium?.url,
          playlist_title: playlist?.title || null,
          playlist_id: playlistId
        }));

      if (data.nextPageToken) {
        this.playlistPageTokens[playlistId] = data.nextPageToken;
      } else {
        delete this.playlistPageTokens[playlistId];
        this.playlistExhausted.add(playlistId);
      }

      // Cache first page result
      if (!pageToken) {
        YTCache.set(YTCache.videosKey(playlistId), {
          videos,
          nextPageToken: data.nextPageToken || null,
          exhausted: !data.nextPageToken,
        }, YTCache.TTL_VIDEOS);
      }

      return { videos, nextPageToken: data.nextPageToken || null };
    } catch (error) {
      console.error(`Failed to fetch playlist ${playlistId}:`, error);
      this.playlistExhausted.add(playlistId);
      return { videos: [], nextPageToken: null };
    }
  }

  async ensurePlaylistVideosLoaded(playlistId) {
    if (this.videoCache.has(playlistId)) {
      const cached = this.videoCache.get(playlistId);
      if (cached.length > 0) return cached;
    }
    const pageToken = this.playlistPageTokens[playlistId] || null;
    const { videos } = await this.fetchPlaylistVideos(playlistId, pageToken);
    if (videos.length > 0) {
      const existing = this.videoCache.get(playlistId) || [];
      const unique = deduplicateById([...existing, ...videos]);
      this.videoCache.set(playlistId, unique);
      return unique;
    }
    return [];
  }

  async fetchYouTubeVideos(append = false) {
    const perPage = parseInt(this.limit) || 6;
    const playlistIds = this.selectedPlaylists.length
      ? this.selectedPlaylists
      : this.allPlaylists.map(pl => pl.id);

    if (!playlistIds.length) return { videos: [], hasMore: false };

    if (!append) {
      await Promise.all(playlistIds.map(id => this.ensurePlaylistVideosLoaded(id)));
      this.isInitialLoad = false;
    } else {
      let hasUnshownInCache = false;
      for (const playlistId of playlistIds) {
        const cached = this.videoCache.get(playlistId) || [];
        if (cached.some(v => !this.displayedVideoIds.has(v.id))) {
          hasUnshownInCache = true;
          break;
        }
      }
      if (!hasUnshownInCache) {
        const toFetch = playlistIds.filter(id => !this.playlistExhausted.has(id) && this.playlistPageTokens[id]);
        if (toFetch.length > 0) {
          const results = await Promise.all(toFetch.map(id => this.fetchPlaylistVideos(id, this.playlistPageTokens[id])));
          results.forEach((result, i) => {
            if (result.videos.length > 0) {
              const existing = this.videoCache.get(toFetch[i]) || [];
              this.videoCache.set(toFetch[i], deduplicateById([...existing, ...result.videos]));
            }
          });
        }
      }
    }

    let allVideos = [];
    for (const id of playlistIds) allVideos = allVideos.concat(this.videoCache.get(id) || []);
    allVideos.sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
    const uniqueVideos = deduplicateById(allVideos);
    const available = append ? uniqueVideos.filter(v => !this.displayedVideoIds.has(v.id)) : uniqueVideos;
    const result = available.slice(0, perPage);
    const hasMore = (result.length === perPage || available.length > perPage) && this.hasMoreVideosAvailable();
    return { videos: result, hasMore };
  }

  renderVideoCard(video) {
    const date = new Date(video.published_at);
    const dateStr = date.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
    const featureImage = video.feature_image ?
      `<a href="${video.url}" class="card__image" target="_blank" rel="noopener">
        <img src="${video.feature_image}" alt="${video.title}">
      </a>` : '';
    const tagHtml = video.playlist_title ? `<span class="card__tag">${video.playlist_title}</span>` : '';

    if (this.cardType === 'default') {
      return `
        <article class="card card--default flex direction-column ${this.cardClass}">
          ${featureImage}
          <div class="card__main flex direction-column">
            <h3 class="card__title">
              <a href="${video.url}" target="_blank" rel="noopener">${video.title}</a>
            </h3>
            <div class="card__footer flex items-center">
              ${tagHtml}
              <div class="card__meta flex items-center">
                <span class="card__date">${dateStr}</span>
              </div>
            </div>
          </div>
        </article>`;
    } else {
      return `
        <article class="card card--small flex items-center ${this.cardClass}">
          ${featureImage}
          <div class="card__main flex">
            <h3 class="card__title">
              <a href="${video.url}" target="_blank" rel="noopener">${video.title}</a>
            </h3>
            ${tagHtml}
            <div class="card__meta flex items-center">
              <span class="card__date">${dateStr}</span>
            </div>
          </div>
        </article>`;
    }
  }

  async loadMorePosts() {
    if (!this.infiniteScroll || this.isLoading || !this.hasMorePosts) return;
    this.isLoading = true;
    try {
      if (YTQuotaState.isExhausted()) {
        const perPage = parseInt(this.limit) || 6;
        const nextBatch = this.offlineVideos.slice(this.offlineDisplayedCount, this.offlineDisplayedCount + perPage);
        if (nextBatch.length > 0) {
          this.offlineDisplayedCount += nextBatch.length;
          nextBatch.forEach(v => this.displayedVideoIds.add(v.id));
          this.renderPosts(nextBatch, true);
          this.hasMorePosts = this.offlineDisplayedCount < this.offlineVideos.length;
        } else {
          this.hasMorePosts = false;
        }
        return;
      }

      if (this.videoBuffer.length > 0) {
        const perPage = parseInt(this.limit) || 6;
        const videosToShow = this.videoBuffer.splice(0, perPage);
        videosToShow.forEach(v => this.displayedVideoIds.add(v.id));
        this.allVideos = [...this.allVideos, ...videosToShow];
        this.renderPosts(videosToShow, true);
        if (this.videoBuffer.length === 0) await this.prefetchMoreVideos();
        this.hasMorePosts = this.videoBuffer.length > 0 || this.hasMoreVideosAvailable();
        return;
      }
      const { videos, hasMore } = await this.fetchYouTubeVideos(true);
      if (videos && videos.length > 0) {
        videos.forEach(v => this.displayedVideoIds.add(v.id));
        this.allVideos = [...this.allVideos, ...videos];
        this.renderPosts(videos, true);
        this.hasMorePosts = hasMore;
        if (hasMore) this.prefetchMoreVideos().catch(err => console.error('Background prefetch failed:', err));
      } else {
        this.hasMorePosts = false;
      }
    } catch (error) {
      console.error('Failed to load more videos:', error);
      this.hasMorePosts = false;
    } finally {
      this.isLoading = false;
    }
  }

  getActivePlaylistIds() {
    return this.selectedPlaylists.length ? this.selectedPlaylists : this.allPlaylists.map(pl => pl.id);
  }

  hasMoreVideosAvailable() {
    const playlistIds = this.getActivePlaylistIds();
    for (const id of playlistIds) {
      const cached = this.videoCache.get(id) || [];
      if (cached.some(v => !this.displayedVideoIds.has(v.id))) return true;
    }
    return playlistIds.some(id => !this.playlistExhausted.has(id) && this.playlistPageTokens[id]);
  }

  async prefetchMoreVideos() {
    if (this.isLoading || !this.hasMoreVideosAvailable()) return;
    try {
      const playlistIds = this.getActivePlaylistIds();
      const active = playlistIds.filter(id => !this.playlistExhausted.has(id) && this.playlistPageTokens[id]);
      if (!active.length) return;

      const results = await Promise.all(active.map(id => this.fetchPlaylistVideos(id, this.playlistPageTokens[id])));
      let newVideos = [];
      results.forEach((result, i) => {
        if (result.videos.length > 0) {
          const existing = this.videoCache.get(active[i]) || [];
          this.videoCache.set(active[i], deduplicateById([...existing, ...result.videos]));
          newVideos = newVideos.concat(result.videos);
        }
      });

      newVideos.sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
      const fresh = deduplicateById(newVideos).filter(v => !this.displayedVideoIds.has(v.id));
      this.videoBuffer = [...this.videoBuffer, ...fresh];
    } catch (error) {
      console.error('Failed to prefetch videos:', error);
    }
  }

  resetInfiniteScrollState() {
    this.allVideos = [];
    this.displayedVideoIds.clear();
    this.videoBuffer = [];
    this.playlistPageTokens = {};
    this.playlistExhausted.clear();
  }

  renderPlaylistsInPopup() {
    const formatsList = this.section.querySelector('#formats-list');
    if (!formatsList) return;
    if (this.popup) {
      const titleEls = this.popup.querySelectorAll('.filter-popup__title');
      if (titleEls[0]) titleEls[0].textContent = 'プレイリスト';
      this.popup.querySelector('#types-list')?.closest('.filter-popup__column')?.style.setProperty('display', 'none');
      this.popup.querySelector('#authors-list')?.closest('.filter-popup__column')?.style.setProperty('display', 'none');
    }
    formatsList.innerHTML = this.allPlaylists.map(pl => `
      <label class="filter-popup__option">
        <input type="checkbox" class="filter-popup__checkbox" data-yplaylist="${pl.id}" ${this.selectedPlaylists.includes(pl.id) ? 'checked' : ''}>
        <span class="filter-popup__text">${pl.title}</span>
      </label>`).join('');
  }

  handlePlaylistChange() {
    const boxes = this.section.querySelectorAll('#formats-list input[data-yplaylist]');
    this.selectedPlaylists = Array.from(boxes).filter(b => b.checked).map(b => b.getAttribute('data-yplaylist'));
    this.updateMainPlaylistButtons();
    this.loadVideos();
  }

  updateMainPlaylistButtons() {
    this.filterList.querySelectorAll('.cards-filter__item-button').forEach(button => {
      const slug = button.getAttribute('data-slug');
      const playlistId = button.getAttribute('data-playlist-id');
      if (slug === 'all') {
        button.classList.toggle('active', this.selectedPlaylists.length === 0);
      } else if (playlistId) {
        button.classList.toggle('active', this.selectedPlaylists.includes(playlistId));
      } else {
        button.classList.remove('active');
      }
    });
  }

  resetAllFilters() {
    this.selectedPlaylists = [];
    this.section.querySelectorAll('.filter-popup__checkbox').forEach(cb => cb.checked = false);
    this.updateMainPlaylistButtons();
    this.closePopup();
    this.loadVideos();
  }
}

// Mixed mode scenario (articles + videos with mode switching).
// YouTube API calls go through the Cloudflare Worker proxy at /api/youtube/*
// — see VideosCardsManager above for full architecture notes.
export class MixedCardsManager extends BaseCardsManager {
  constructor(section, config) {
    super(section, config);
    this.youtubeChannelId = config.youtubeChannelId;
    this.isVideosMode = false;
    this.allPlaylists = [];
    this.selectedPlaylists = [];
    this.originalViewAllHref = '';
    this._articlesSnapshot = null;
    this._videosSnapshot = null;
  }

  setupEventListeners() {
    super.setupEventListeners();

    this.section.querySelectorAll('.cards-list__mode-button').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        this.switchMode(btn.getAttribute('data-mode'), false);
      });
    });

    if (this.filterList) {
      this.filterList.addEventListener('click', e => {
        const button = e.target.closest('button.cards-filter__item-button');
        if (!button) return;
        e.preventDefault();
        this.handleFilterClick(button);
      });
    }

    this.setupFilterPopup();
  }

  handleCheckboxChange(e) {
    if (e.target.hasAttribute('data-yplaylist')) {
      if (this.isVideosMode) this.handlePlaylistChange();
    } else {
      if (!this.isVideosMode) this.handleFilterChange();
    }
  }

  onPopupOpen() {
    if (this.isVideosMode) {
      this.renderPlaylistsInPopup();
    } else {
      this.restorePopupForArticles();
      this.renderFormatsList();
      if (this.types) this.renderTypesList();
      if (this.filterByAuthors) this.renderAuthorsList();
    }
  }

  onFilterChanged() {
    this.updateMainButtons();
    this.loadPostsByTag('all');
  }

  renderItem(item) {
    return this.isVideosMode ? this.renderVideoCard(item) : this.renderCard(item);
  }

  async loadInitialContent() {
    this.isVideosMode = false;

    const modeButtons = this.section.querySelectorAll('.cards-list__mode-button');
    modeButtons.forEach(btn => btn.classList.remove('active'));
    const articlesButton = this.section.querySelector('.cards-list__mode-button[data-mode="articles"]');
    if (articlesButton) articlesButton.classList.add('active');

    await this.loadFormats();
    this.renderFormatButtons();

    await Promise.all([
      this.types ? this.loadTypes() : Promise.resolve(),
      this.filterByAuthors ? this.loadAuthors() : Promise.resolve(),
    ]);

    const viewAllButton = this.section.querySelector('.cards-list__view-all');
    if (viewAllButton) this.originalViewAllHref = viewAllButton.getAttribute('href') || '/articles';
  }

  updateViewAllButton(newHref) {
    const viewAllButton = this.section.querySelector('.cards-list__view-all');
    if (viewAllButton) viewAllButton.setAttribute('href', newHref);
  }

  activateAllButton() {
    if (!this.filterList) return;
    this.filterList.querySelectorAll('.cards-filter__item-button').forEach(btn => btn.classList.remove('active'));
    const allBtn = this.filterList.querySelector('.cards-filter__item-button[data-slug="all"]');
    if (allBtn) allBtn.classList.add('active');
  }

  async switchMode(mode, skipContentLoad = false) {
    const modeButtons = this.section.querySelectorAll('.cards-list__mode-button');
    modeButtons.forEach(btn => btn.classList.toggle('active', btn.getAttribute('data-mode') === mode));

    const wasVideosMode = this.isVideosMode;
    this.isVideosMode = mode === 'videos';
    this.closePopup();

    // Save current content snapshot before clearing (using previous mode state)
    if (this.isVideosMode && !wasVideosMode) {
      this._articlesSnapshot = {
        html: this.articlesContainer.innerHTML,
        filterHtml: this.filterList?.innerHTML,
        allPosts: this.allPosts,
        currentPage: this.currentPage,
        hasMorePosts: this.hasMorePosts,
        selectedFormats: [...this.selectedFormats],
        selectedTypes: [...this.selectedTypes],
        selectedAuthors: [...this.selectedAuthors],
      };
    } else if (!this.isVideosMode && wasVideosMode) {
      this._videosSnapshot = {
        html: this.articlesContainer.innerHTML,
        filterHtml: this.filterList?.innerHTML,
        selectedPlaylists: [...this.selectedPlaylists],
      };
    }

    // Reset filter state
    this.selectedFormats = [];
    this.selectedTypes = [];
    this.selectedAuthors = [];
    this.selectedPlaylists = [];
    this.section.querySelectorAll('.filter-popup__checkbox').forEach(cb => cb.checked = false);

    // Lock height and fade out before clearing
    const currentHeight = this.articlesContainer.offsetHeight;
    this.articlesContainer.style.minHeight = `${currentHeight}px`;
    this.articlesContainer.style.opacity = '0';
    this.articlesContainer.innerHTML = '';
    this.articlesContainer.classList.remove('cards-list__articles--empty', 'cards-list__articles--loading', 'cards-list__articles--error');

    // Restore filter section visibility in case offline mode hid it
    const filterSection = this.section.querySelector('.cards-list__filter');
    if (filterSection) filterSection.style.display = '';

    if (this.isVideosMode) {
      await this.loadPlaylists();
      this.updateViewAllButton('/videos');
      if (this._videosSnapshot && !skipContentLoad) {
        const s = this._videosSnapshot;
        if (this.filterList) this.filterList.innerHTML = s.filterHtml;
        this.selectedPlaylists = s.selectedPlaylists;
        this.articlesContainer.innerHTML = s.html;
        this.articlesContainer.style.minHeight = '';
        this.articlesContainer.style.opacity = '1';
      } else {
        this.renderPlaylistButtons();
        this.activateAllButton();
        if (!skipContentLoad) this.loadVideos();
      }
    } else {
      await this.loadFormats();
      this.updateViewAllButton(this.originalViewAllHref || '/articles');
      if (this._articlesSnapshot && !skipContentLoad) {
        const s = this._articlesSnapshot;
        if (this.filterList) this.filterList.innerHTML = s.filterHtml;
        this.selectedFormats = s.selectedFormats;
        this.selectedTypes = s.selectedTypes;
        this.selectedAuthors = s.selectedAuthors;
        this.allPosts = s.allPosts;
        this.currentPage = s.currentPage;
        this.hasMorePosts = s.hasMorePosts;
        this.articlesContainer.innerHTML = s.html;
        this.articlesContainer.style.minHeight = '';
        this.articlesContainer.style.opacity = '1';
      } else {
        this.renderFormatButtons();
        this.activateAllButton();
        if (!skipContentLoad) this.loadPostsByTag('all');
      }
    }
  }

  async loadPlaylists() {
    if (this.allPlaylists.length > 0) return;
    if (!this.youtubeChannelId || this.youtubeChannelId.includes('{{') || this.youtubeChannelId === 'undefined') {
      console.warn('YouTube channel ID not configured or invalid');
      this.allPlaylists = [];
      return;
    }
    const cacheKey = YTCache.playlistsKey(this.youtubeChannelId);
    const cached = YTCache.get(cacheKey);
    if (cached) {
      this.allPlaylists = cached.filter(pl => pl.title.toLowerCase() !== 'shorts');
      return;
    }
    if (YTQuotaState.isExhausted()) {
      this.allPlaylists = [];
      return;
    }
    try {
      const url = `/api/youtube/playlists?part=snippet,contentDetails&channelId=${this.youtubeChannelId}&maxResults=50`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.error) {
        if (data.error.code === 403) YTQuotaState.setExhausted();
        else console.error('YouTube API error:', data.error);
        this.allPlaylists = [];
        return;
      }
      if (data.items) {
        this.allPlaylists = data.items
          .map(pl => ({
            id: pl.id,
            title: pl.snippet.title,
            count: pl.contentDetails?.itemCount || 0
          }))
          .filter(pl => pl.title.toLowerCase() !== 'shorts');
        YTCache.set(cacheKey, this.allPlaylists, YTCache.TTL_PLAYLISTS);
      }
    } catch (error) {
      console.error('Failed to load playlists:', error);
      this.allPlaylists = [];
    }
  }

  renderPlaylistButtons() {
    if (!this.filterList) return;
    const topPlaylists = [...this.allPlaylists].sort((a, b) => b.count - a.count).slice(0, 4);
    const allButton = this.filterList.querySelector('li:first-child');
    this.filterList.innerHTML = '';
    if (allButton) this.filterList.appendChild(allButton);
    this.filterList.insertAdjacentHTML('beforeend', topPlaylists.map(pl =>
      `<li class="cards-filter__item">
        <button class="cards-filter__item-button" data-playlist-id="${pl.id}">${pl.title}</button>
      </li>`
    ).join(''));
  }

  renderFormatButtons() {
    if (!this.filterList) return;
    const allButton = this.filterList.querySelector('li:first-child');
    this.filterList.innerHTML = '';
    if (allButton) this.filterList.appendChild(allButton);
    this.filterList.insertAdjacentHTML('beforeend', this.allFormats.map(format =>
      `<li class="cards-filter__item">
        <button class="cards-filter__item-button" data-slug="${format.slug}">${format.name}</button>
      </li>`
    ).join(''));
  }

  handleFilterClick(button) {
    if (this.isVideosMode) {
      this.handleVideoFilterClick(button);
    } else {
      this.handleArticleFilterClick(button);
    }
  }

  handleArticleFilterClick(button) {
    const slug = button.getAttribute('data-slug');
    this.filterList.querySelectorAll('.cards-filter__item-button').forEach(btn => btn.classList.remove('active'));
    button.classList.add('active');

    this.selectedFormats = [];
    this.selectedTypes = [];
    this.selectedAuthors = [];
    if (slug !== 'all') {
      const format = this.allFormats.find(f => f.slug === slug);
      if (format) this.selectedFormats = [format];
    }

    this.closePopup();
    this.updatePopupCheckboxes();
    this.loadPostsByTag(slug);
  }

  handleVideoFilterClick(button) {
    const playlistId = button.getAttribute('data-playlist-id');
    const slug = button.getAttribute('data-slug');
    this.filterList.querySelectorAll('.cards-filter__item-button').forEach(btn => btn.classList.remove('active'));
    button.classList.add('active');
    if (slug === 'all') {
      this.selectedPlaylists = [];
    } else if (playlistId) {
      this.selectedPlaylists = [playlistId];
    }
    this.articlesContainer.style.minHeight = `${this.articlesContainer.offsetHeight}px`;
    this.articlesContainer.style.transition = 'opacity 0.15s ease';
    this.articlesContainer.style.opacity = '0';
    this.loadVideos();
  }

  handlePlaylistChange() {
    if (!this.isVideosMode) {
      console.warn('handlePlaylistChange called but not in videos mode');
      return;
    }
    const boxes = this.section.querySelectorAll('#formats-list input[data-yplaylist]');
    this.selectedPlaylists = Array.from(boxes).filter(b => b.checked).map(b => b.getAttribute('data-yplaylist'));
    this.updateMainPlaylistButtons();
    this.articlesContainer.style.minHeight = `${this.articlesContainer.offsetHeight}px`;
    this.articlesContainer.style.transition = 'opacity 0.15s ease';
    this.articlesContainer.style.opacity = '0';
    this.loadVideos();
  }

  updateMainButtons() {
    if (!this.filterList) return;
    if (this.isVideosMode) {
      this.updateMainPlaylistButtons();
    } else {
      this.filterList.querySelectorAll('.cards-filter__item-button').forEach(button => {
        const slug = button.getAttribute('data-slug');
        if (slug === 'all') {
          button.classList.toggle('active', this.selectedFormats.length === 0);
        } else {
          button.classList.toggle('active', this.selectedFormats.some(f => f.slug === slug));
        }
      });
    }
  }

  updateMainPlaylistButtons() {
    this.filterList.querySelectorAll('.cards-filter__item-button').forEach(button => {
      const slug = button.getAttribute('data-slug');
      const playlistId = button.getAttribute('data-playlist-id');
      if (slug === 'all') {
        button.classList.toggle('active', this.selectedPlaylists.length === 0);
      } else if (playlistId) {
        button.classList.toggle('active', this.selectedPlaylists.includes(playlistId));
      } else {
        button.classList.remove('active');
      }
    });
  }

  renderPlaylistsInPopup() {
    const formatsList = this.section.querySelector('#formats-list');
    if (!formatsList) return;
    if (this.popup) {
      const titleEls = this.popup.querySelectorAll('.filter-popup__title');
      if (titleEls[0]) titleEls[0].textContent = 'プレイリスト';
      this.popup.querySelector('#types-list')?.closest('.filter-popup__column')?.style.setProperty('display', 'none');
      this.popup.querySelector('#authors-list')?.closest('.filter-popup__column')?.style.setProperty('display', 'none');
    }
    formatsList.innerHTML = this.allPlaylists.map(pl => `
      <label class="filter-popup__option">
        <input type="checkbox" class="filter-popup__checkbox" data-yplaylist="${pl.id}" ${this.selectedPlaylists.includes(pl.id) ? 'checked' : ''}>
        <span class="filter-popup__text">${pl.title}</span>
      </label>`).join('');
  }

  restorePopupForArticles() {
    if (!this.popup) return;
    const titleEls = this.popup.querySelectorAll('.filter-popup__title');
    if (titleEls[0]) titleEls[0].textContent = this.formatsLabel || '形式';
    this.popup.querySelector('#types-list')?.closest('.filter-popup__column')?.style.setProperty('display', '');
    this.popup.querySelector('#authors-list')?.closest('.filter-popup__column')?.style.setProperty('display', '');
  }

  loadVideos() {
    if (YTQuotaState.isExhausted()) {
      this.loadVideosOffline();
    } else {
      this.loadVideosOnline();
    }
  }

  async loadVideosOnline() {
    try {
      this.articlesContainer.classList.add('cards-list__articles--loading');
      this.articlesContainer.classList.remove('cards-list__articles--empty', 'cards-list__articles--error');

      let { videos } = await this.fetchYouTubeVideos();

      if (!this.isVideosMode) return;

      // If quota was hit during fetch, switch to offline mode
      if (YTQuotaState.isExhausted()) {
        await this.loadVideosOffline();
        return;
      }

      this.articlesContainer.classList.remove('cards-list__articles--loading');


      const html = videos.length > 0
        ? videos.map(v => this.renderVideoCard(v)).join('')
        : '<p class="cards-list__empty-message">動画が見つかりませんでした。</p>';

      if (videos.length === 0) this.articlesContainer.classList.add('cards-list__articles--empty');
      else this.articlesContainer.classList.remove('cards-list__articles--empty', 'cards-list__articles--error');

      this.articlesContainer.innerHTML = html;
      this.articlesContainer.style.minHeight = '';
      this.articlesContainer.style.transition = 'opacity 0.15s ease';
      this.articlesContainer.style.opacity = '1';
    } catch (error) {
      if (!this.isVideosMode) return;
      console.error('Failed to load videos:', error);
      this.articlesContainer.classList.remove('cards-list__articles--loading');
      this.articlesContainer.classList.add('cards-list__articles--error');
      this.articlesContainer.innerHTML = '<p class="cards-list__error-message">動画の読み込みに失敗しました。時間をおいて再度お試しください。</p>';
      this.articlesContainer.style.minHeight = '';
      this.articlesContainer.style.transition = 'opacity 0.15s ease';
      this.articlesContainer.style.opacity = '1';
    }
  }

  async loadVideosOffline() {
    if (!this.isVideosMode) return;
    try {
      // Lock height and fade out before swapping content to prevent layout jump
      const h = this.articlesContainer.offsetHeight;
      if (h > 0) this.articlesContainer.style.minHeight = `${h}px`;
      this.articlesContainer.style.transition = 'opacity 0.15s ease';
      this.articlesContainer.style.opacity = '0';
      await new Promise(r => setTimeout(r, 160));
      if (!this.isVideosMode) return;

      // Clear content first — before removing --empty (which holds centering), so nothing jumps
      this.articlesContainer.innerHTML = '';
      this.articlesContainer.classList.remove('cards-list__articles--empty', 'cards-list__articles--error');

      const perPage = parseInt(this.limit) || 4;

      let videos;
      if (this.selectedPlaylists.length > 0) {
        const raw = [];
        for (const id of this.selectedPlaylists) {
          const cached = YTCache.get(YTCache.videosKey(id));
          if (cached?.videos) raw.push(...cached.videos);
        }
        videos = deduplicateById(raw).sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
      } else {
        videos = YTCache.getAllCachedVideos();
      }

      if (!this.isVideosMode) return;
      this.articlesContainer.classList.remove('cards-list__articles--loading');

      if (videos.length === 0 && this.selectedPlaylists.length === 0) {
        // No cached videos at all — hide filter controls
        const filterSection = this.section.querySelector('.cards-list__filter');
        if (filterSection) filterSection.style.display = 'none';
        this.articlesContainer.classList.add('cards-list__articles--empty');
        this.articlesContainer.innerHTML = '<p class="cards-list__empty-message">動画が一時的に利用できません。復旧に向けて作業中です。しばらく経ってから再度お試しください。</p>';
      } else if (videos.length === 0) {
        this.articlesContainer.classList.add('cards-list__articles--empty');
        this.articlesContainer.innerHTML = '<p class="cards-list__empty-message">このプレイリストの動画はまだキャッシュされていません。復旧に向けて作業中です。しばらく経ってから再度お試しください。</p>';
      } else {
        this.articlesContainer.classList.remove('cards-list__articles--empty', 'cards-list__articles--error');
        this.articlesContainer.innerHTML = videos.slice(0, perPage).map(v => this.renderVideoCard(v)).join('');
      }

      this.articlesContainer.style.minHeight = '';
      this.articlesContainer.style.transition = '';
      this.articlesContainer.style.opacity = '1';
    } catch (error) {
      if (!this.isVideosMode) return;
      console.error('Failed to load cached videos:', error);
      this.articlesContainer.classList.remove('cards-list__articles--loading');
      this.articlesContainer.classList.add('cards-list__articles--error');
      this.articlesContainer.innerHTML = '<p class="cards-list__error-message">動画の読み込みに失敗しました。時間をおいて再度お試しください。</p>';
      this.articlesContainer.style.minHeight = '';
      this.articlesContainer.style.transition = '';
      this.articlesContainer.style.opacity = '1';
    }
  }

  async fetchYouTubeVideos() {
    const perPage = parseInt(this.limit) || 6;
    const playlistIds = this.selectedPlaylists.length
      ? this.selectedPlaylists
      : this.allPlaylists.map(pl => pl.id);

    if (!playlistIds.length) return { videos: [] };

    const results = await Promise.all(playlistIds.map(async (playlistId) => {
      const playlist = this.allPlaylists.find(p => p.id === playlistId);
      if (!playlist) return { videos: [] };

      const cacheKey = YTCache.videosKey(playlistId);
      const cached = YTCache.get(cacheKey);
      if (cached) return { videos: cached.videos };

      try {
        const url = `/api/youtube/playlistItems?part=snippet,contentDetails,status&maxResults=12&playlistId=${playlistId}`;
        const response = await fetch(url);
        const data = await response.json();
        if (data.error) {
          if (data.error.code === 403) YTQuotaState.setExhausted();
          return { videos: [] };
        }
        if (!data.items) return { videos: [] };
        const videos = data.items
          .filter(item => {
            const title = item.snippet?.title || '';
            if (!title || title === 'Deleted video' || title === 'Private video' || title === 'This video is unavailable') return false;
              if (item.status?.privacyStatus) return item.status.privacyStatus === 'public';
            return true;
          })
          .map(item => ({
            id: item.contentDetails?.videoId || item.snippet?.resourceId?.videoId,
            url: `https://www.youtube.com/watch?v=${item.contentDetails?.videoId || item.snippet?.resourceId?.videoId}`,
            title: item.snippet.title,
            published_at: item.snippet.publishedAt,
            feature_image: item.snippet.thumbnails?.maxres?.url ||
                           item.snippet.thumbnails?.standard?.url ||
                           item.snippet.thumbnails?.high?.url ||
                           item.snippet.thumbnails?.medium?.url,
            playlist_title: playlist?.title || null
          }));
        YTCache.set(cacheKey, { videos, nextPageToken: data.nextPageToken || null, exhausted: !data.nextPageToken }, YTCache.TTL_VIDEOS);
        return { videos };
      } catch (error) {
        console.error(`Failed to fetch playlist ${playlistId}:`, error);
        return { videos: [] };
      }
    }));

    let allVideos = results.flatMap(r => r.videos);
    allVideos.sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
    return { videos: deduplicateById(allVideos).slice(0, perPage) };
  }

  renderVideoCard(video) {
    const date = new Date(video.published_at);
    const dateStr = date.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
    const featureImage = video.feature_image ?
      `<a href="${video.url}" class="card__image" target="_blank" rel="noopener">
        <img src="${video.feature_image}" alt="${video.title}">
      </a>` : '';
    const tagHtml = video.playlist_title ? `<span class="card__tag">${video.playlist_title}</span>` : '';

    if (this.cardType === 'default') {
      return `
        <article class="card card--default flex direction-column ${this.cardClass}">
          ${featureImage}
          <div class="card__main flex direction-column">
            <h3 class="card__title">
              <a href="${video.url}" target="_blank" rel="noopener">${video.title}</a>
            </h3>
            <div class="card__footer flex items-center">
              ${tagHtml}
              <div class="card__meta flex items-center">
                <span class="card__date">${dateStr}</span>
              </div>
            </div>
          </div>
        </article>`;
    } else {
      return `
        <article class="card card--small flex items-center ${this.cardClass}">
          ${featureImage}
          <div class="card__main flex">
            <h3 class="card__title">
              <a href="${video.url}" target="_blank" rel="noopener">${video.title}</a>
            </h3>
            ${tagHtml}
            <div class="card__meta flex items-center">
              <span class="card__date">${dateStr}</span>
            </div>
          </div>
        </article>`;
    }
  }

  async loadPostsByTag(slug, page = 1, append = false) {
    if (this.isLoading) return;
    try {
      this.isLoading = true;

      let filter = '';
      if (slug !== 'all') {
        filter = `tag:${slug}`;
      } else if (this.filterByFormats && this.formats) {
        filter = `tag:[${this.formats}]`;
      }

      if (this.selectedFormats.length > 0) {
        const f = this.selectedFormats.map(f => f.slug).join(',');
        filter = filter ? `${filter}+tag:[${f}]` : `tag:[${f}]`;
      }
      if (this.selectedTypes.length > 0) {
        const t = this.selectedTypes.map(t => t.slug).join(',');
        filter = filter ? `${filter}+tag:[${t}]` : `tag:[${t}]`;
      }
      if (this.selectedAuthors.length > 0) {
        const slugs = this.selectedAuthors.map(a => a.slug).join(',');
        const authorFilter = this.selectedAuthors.length === 1
          ? `authors:${slugs}`
          : `authors:[${slugs}]`;
        filter = filter ? `${filter}+${authorFilter}` : authorFilter;
      }
      if (this.excludeFormats) {
        const exclude = `tag:-[${this.excludeFormats}]`;
        filter = filter ? `${filter}+${exclude}` : exclude;
      }
      filter = filter ? `${filter}+tag:-hash-hide` : 'tag:-hash-hide';

      const options = {
        include: ['tags', 'authors'],
        page,
        limit: this.limit === 'all' ? 15 : parseInt(this.limit)
      };
      if (filter) options.filter = filter;

      const posts = await this.api.posts.browse(options);

      if (append) {
        this.allPosts = [...this.allPosts, ...posts];
        this.renderPosts(posts, true);
        this.hasMorePosts = posts.length === parseInt(this.limit);
        this.currentPage = page;
      } else {
        this.allPosts = posts;
        this.renderPosts(posts, false);
        this.hasMorePosts = posts.length === parseInt(this.limit);
        this.currentPage = 1;
      }
    } catch (error) {
      console.error('Failed to load posts:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async loadMorePosts() {
    if (!this.infiniteScroll || this.isLoading || !this.hasMorePosts) return;
    if (this.isVideosMode) return;
    const activeButton = this.filterList.querySelector('.cards-filter__item-button.active');
    const currentSlug = activeButton?.getAttribute('data-slug') ?? 'all';
    await this.loadPostsByTag(currentSlug, this.currentPage + 1, true);
  }

  resetAllFilters() {
    this.selectedFormats = [];
    this.selectedTypes = [];
    this.selectedAuthors = [];
    this.selectedPlaylists = [];
    this.section.querySelectorAll('.filter-popup__checkbox').forEach(cb => cb.checked = false);
    if (this.filterList) {
      this.filterList.querySelectorAll('.cards-filter__item-button').forEach(btn => {
        btn.classList.toggle('active', btn.getAttribute('data-slug') === 'all');
      });
    }
    this.closePopup();
    if (this.isVideosMode) {
      this.loadVideos();
    } else {
      this.loadPostsByTag('all');
    }
  }
}

// Author page scenario
export class AuthorCardsManager extends BaseCardsManager {
  constructor(section, config) {
    super(section, config);
    this.authorSlug = section.getAttribute('data-author-slug');
    this.allTags = [];
  }

  setupEventListeners() {
    super.setupEventListeners();
    if (this.filterList) {
      this.filterList.addEventListener('click', e => {
        const button = e.target.closest('button.cards-filter__item-button');
        if (!button) return;
        e.preventDefault();
        this.handleFilterClick(button);
      });
    }
  }

  async loadInitialContent() {
    await this.loadAuthorTags();
  }

  async loadAuthorTags() {
    try {
      // Only the primary tag is read below, so project the post body away — see the
      // note in loadAuthors (OPS-1096).
      const response = await this.fetchAllPosts({
        filter: `authors:${this.authorSlug}+tag:-hash-hide`,
        include: ['tags'],
        fields: 'id'
      });
      const tagsMap = new Map();
      response.forEach(post => {
        // `fields` strips the computed primary_tag, so rebuild it the way Ghost does:
        // the first public tag. Same approach as withPrimaryTag() in search.js.
        const primaryTag = (post.tags || []).find(tag => tag.visibility === 'public');
        if (primaryTag) {
          tagsMap.set(primaryTag.slug, {
            slug: primaryTag.slug,
            name: primaryTag.name
          });
        }
      });
      this.allTags = Array.from(tagsMap.values());
      this.renderTagButtons();
    } catch (error) {
      console.error('Failed to load author tags:', error);
    }
  }

  renderTagButtons() {
    if (!this.filterList) return;
    const allButton = this.filterList.querySelector('li:first-child');
    this.filterList.innerHTML = '';
    if (allButton) this.filterList.appendChild(allButton);
    this.filterList.insertAdjacentHTML('beforeend', this.allTags.map(tag =>
      `<li class="cards-filter__item">
        <button class="cards-filter__item-button" data-slug="${tag.slug}">${tag.name}</button>
      </li>`
    ).join(''));
  }

  handleFilterClick(button) {
    const slug = button.getAttribute('data-slug');
    this.filterList.querySelectorAll('.cards-filter__item-button').forEach(btn => btn.classList.remove('active'));
    button.classList.add('active');
    this.loadPostsByTag(slug);
  }

  async loadPostsByTag(slug, page = 1, append = false) {
    if (this.isLoading) return;
    try {
      this.isLoading = true;
      let filter = `authors:${this.authorSlug}+tag:-hash-hide`;
      if (slug !== 'all') filter += `+tag:${slug}`;

      const options = {
        include: ['tags', 'authors'],
        page,
        limit: this.limit === 'all' ? 15 : parseInt(this.limit),
        filter
      };

      const posts = await this.api.posts.browse(options);

      if (append) {
        const existingIds = new Set(this.allPosts.map(p => p.id));
        const newPosts = posts.filter(p => !existingIds.has(p.id));
        this.allPosts = [...this.allPosts, ...newPosts];
        this.renderPosts(newPosts, true);
      } else {
        this.allPosts = posts;
        this.renderPosts(posts, false);
      }

      this.currentPage = page;
      this.hasMorePosts = posts.length === parseInt(this.limit);
    } catch (error) {
      console.error('Failed to load posts:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async loadMorePosts() {
    if (!this.infiniteScroll || this.isLoading || !this.hasMorePosts) return;
    const activeButton = this.filterList.querySelector('.cards-filter__item-button.active');
    const currentSlug = activeButton?.getAttribute('data-slug') ?? 'all';
    await this.loadPostsByTag(currentSlug, this.currentPage + 1, true);
  }
}

// Initialize the cards list manager
export function initCardsListFilter() {
  const sections = document.querySelectorAll(".cards-list[data-scenario]");
  if (!sections.length) return;
  new CardsListManager();
}
